import { z } from 'zod';
import { SixyncTelestrationStrokePayloadSchema } from './Telestration';
import { CfbNarrationCueSchema, CfbPlaybackStateSchema } from './CfbSync';

export const SIXYNC_PROTOCOL = 'sixync.v1' as const;
export const SIXYNC_TELEGAMES_LIVE_ROOM_PROFILE = 'sixync.profile.telegames.live-room.v0' as const;

export const SIXYNC_RELIABILITY_CLASSES = [
    'reliable_ordered',
    'latest',
    'ephemeral',
] as const;

export const SIXYNC_SOURCE_KINDS = [
    'desktop_av',
    'stage_media',
    'room_music',
    'activity_surface',
    'web_content',
    'program_web_surface',
    'html_surface',
    'companion_panel',
    'poll_surface',
    'scoreboard',
    'lower_third',
    'participant_camera',
    'participant_microphone',
    'agent_avatar',
    'agent_voice',
    // #3844 — a HUMAN rendered as a companion avatar (a puppet). Distinct from
    // `agent_avatar`: it has a participant's voice, a participant's nameplate, and no
    // companion behaviour, so a consumer must not treat it as a cast member.
    'participant_avatar',
    'overlay',
    'reaction',
] as const;

export const SIXYNC_SOURCE_STATUSES = [
    'announced',
    'ready',
    'stalled',
    'stopped',
] as const;

export const SIXYNC_TRANSPORT_KINDS = [
    'media_server',
    'web_content',
    'peer',
    'external',
    'generated',
    'none',
] as const;

export const SIXYNC_SCENE_LAYER_KINDS = [
    'background',
    'active_media_surface',
    'activity_surface',
    'web_content_surface',
    'program_web_surface',
    'html_surface',
    'companion_panel',
    'poll_surface',
    'scoreboard',
    'lower_third',
    'participant_pip',
    'companion_pip',
    'overlay',
    'caption',
    'reaction',
    'diagnostics_hidden',
] as const;

export const SIXYNC_TARGET_SURFACE_KINDS = [
    'stage',
    'media',
    'activity',
    'participant',
    'companion',
    'overlay',
] as const;

export const SIXYNC_CUE_KINDS = [
    'telestration_stroke',
    'reaction',
    'sentiment',
    'caption',
    'lower_third',
    'activity_animation',
    'agent_gesture',
    'agent_viseme_sequence',
    'companion_mouth_track',
    // #3065 — the companion's conversational state. A CUE rather than source metadata:
    // it varies over a session the way `agent_viseme_sequence` does, and updating source
    // metadata would mean a `source_ready` per state change.
    'companion_state',
    // The reply's emotion (`COMPANION_EMOTION_UPDATE`), carried so the compositor's companion
    // wears the same face the browser does. One cue per companion, replaced in place.
    'companion_emotion',
    'topic_transition',
] as const;

export const SIXYNC_CUE_STATUSES = [
    'active',
    'cancelled',
    'completed',
] as const;

export const SIXYNC_ACTOR_KINDS = [
    'host',
    'moderator',
    'participant',
    'agent',
    'activity',
    'audience',
    'program_publisher',
    'system',
] as const;

export const SIXYNC_EVENT_SCOPES = [
    'source',
    'scene',
    'surface',
    'cue',
    'intent',
    'profile',
    'diagnostics',
] as const;

export const SIXYNC_OPERATIONS = [
    'source_announced',
    'source_ready',
    'source_stalled',
    'source_stopped',
    'scene_layout_set',
    'scene_layer_set',
    'scene_layer_removed',
    'surface_ready',
    'surface_unready',
    'cue_started',
    'cue_cancelled',
    'intent_proposed',
    'intent_accepted',
    'intent_rejected',
    'perception_profile_set',
] as const;

export const SIXYNC_PERCEPTION_PROFILES = [
    'live_room_participant',
    'room_mux_720p24',
    'room_mux_720p30',
    'room_mux_1080p30',
    'audience_safe_overlay',
    'recording_metadata',
    'moderator_diagnostics',
] as const;

export const SIXYNC_PROGRAM_LAYOUT_ROLES = [
    'primary',
    'speaker',
    'companion',
    'pip',
    'overlay',
    'effect',
] as const;

export const SIXYNC_PROGRAM_LAYOUT_VISIBILITIES = [
    'visible',
    'hidden',
    'standby',
] as const;

export const SIXYNC_PROGRAM_LAYOUT_Z_INTENTS = [
    'background',
    'content',
    'foreground',
    'overlay',
    'effect',
] as const;

export const SIXYNC_PROGRAM_PRESENTATION_SCALE_MODES = [
    'cover',
    'contain',
] as const;

export const DEFAULT_SIXYNC_PROGRAM_LAYOUT_TEMPLATE_ID = 'program.default' as const;
export const DEFAULT_SIXYNC_PROGRAM_LAYOUT_MODE = 'default' as const;
export const DEFAULT_SIXYNC_PROGRAM_LAYOUT_PRIORITY = 1_000;

export const SixyncProtocolSchema = z.literal(SIXYNC_PROTOCOL);
export const SixyncProfileSchema = z.literal(SIXYNC_TELEGAMES_LIVE_ROOM_PROFILE);
export const SixyncReliabilitySchema = z.enum(SIXYNC_RELIABILITY_CLASSES);
export const SixyncSourceKindSchema = z.enum(SIXYNC_SOURCE_KINDS);
export const SixyncSourceStatusSchema = z.enum(SIXYNC_SOURCE_STATUSES);
export const SixyncTransportKindSchema = z.enum(SIXYNC_TRANSPORT_KINDS);
export const SixyncSceneLayerKindSchema = z.enum(SIXYNC_SCENE_LAYER_KINDS);
export const SixyncTargetSurfaceKindSchema = z.enum(SIXYNC_TARGET_SURFACE_KINDS);
export const SixyncCueKindSchema = z.enum(SIXYNC_CUE_KINDS);
export const SixyncCueStatusSchema = z.enum(SIXYNC_CUE_STATUSES);
export const SixyncActorKindSchema = z.enum(SIXYNC_ACTOR_KINDS);
export const SixyncEventScopeSchema = z.enum(SIXYNC_EVENT_SCOPES);
export const SixyncOperationSchema = z.enum(SIXYNC_OPERATIONS);
export const SixyncPerceptionProfileSchema = z.enum(SIXYNC_PERCEPTION_PROFILES);
export const SixyncProgramLayoutRoleSchema = z.enum(SIXYNC_PROGRAM_LAYOUT_ROLES);
export const SixyncProgramLayoutVisibilitySchema = z.enum(SIXYNC_PROGRAM_LAYOUT_VISIBILITIES);
export const SixyncProgramLayoutZIntentSchema = z.enum(SIXYNC_PROGRAM_LAYOUT_Z_INTENTS);
export const SixyncProgramPresentationScaleModeSchema = z.enum(SIXYNC_PROGRAM_PRESENTATION_SCALE_MODES);

export const SixyncMetadataSchema = z.record(z.unknown());

export const SixyncProgramAudioClockAnchorSchema = z.object({
    domain: z.literal('program_audio_pts'),
    ptsMs: z.number().nonnegative(),
    sourceId: z.string().min(1).optional(),
    wallTimeMs: z.number().nonnegative().optional(),
});

export const SixyncVisemeEventSchema = z.object({
    startSec: z.number().nonnegative(),
    durationSec: z.number().nonnegative(),
    symbol: z.string().min(1),
});

export const SixyncAgentVisemeSequencePayloadSchema = z.object({
    audioId: z.string().min(1),
    companionId: z.string().min(1),
    clockAnchor: SixyncProgramAudioClockAnchorSchema,
    provider: z.string().min(1),
    events: z.array(SixyncVisemeEventSchema),
});

/**
 * Server-baked mouth track (naturalLipsync M1): per-utterance blendshape
 * frames generated once in signaling from the phoneme timeline via
 * @telegamez/lipsync, played by dumb interpolators on every surface.
 * Frames are utterance-relative; playback is keyed to the same
 * program-audio clock anchor as agent_viseme_sequence.
 */
export const SixyncCompanionMouthTrackPayloadSchema = z.object({
    audioId: z.string().min(1),
    companionId: z.string().min(1),
    clockAnchor: SixyncProgramAudioClockAnchorSchema,
    provider: z.string().min(1),
    /** Which generator baked this track (A/B provenance, e.g. "browser-clone-v1"). */
    generator: z.string().min(1),
    fps: z.number().int().positive(),
    /** Channel names, one per column of each frame row (all-zero channels pruned at publish). */
    channels: z.array(z.string().min(1)).min(1),
    /** frames[frameIndex][channelIndex] — morph weights; jawRotationDelta is radians. */
    frames: z.array(z.array(z.number())),
});

export const SixyncRectSchema = z.object({
    x: z.number().min(0).max(1),
    y: z.number().min(0).max(1),
    width: z.number().min(0).max(1),
    height: z.number().min(0).max(1),
});

export const SixyncActorSchema = z.object({
    actorId: z.string().min(1),
    kind: SixyncActorKindSchema,
    sessionId: z.string().min(1).optional(),
    displayName: z.string().min(1).optional(),
});

export const SixyncTransportSchema = z.object({
    kind: SixyncTransportKindSchema,
    streamPath: z.string().min(1).optional(),
    sourceKind: z.string().min(1).optional(),
    whipUrl: z.string().min(1).optional(),
    whepUrl: z.string().min(1).optional(),
    surfaceUrl: z.string().min(1).optional(),
    webUrl: z.string().min(1).optional(),
    routePath: z.string().min(1).optional(),
    route: z.string().min(1).optional(),
    url: z.string().min(1).optional(),
    moqWtUrl: z.string().min(1).nullable().optional(),
    moqWtAacUrl: z.string().min(1).nullable().optional(),
    hlsUrl: z.string().min(1).nullable().optional(),
    metadata: SixyncMetadataSchema.optional(),
});

export const SixyncSourceSchema = z.object({
    sourceId: z.string().min(1),
    kind: SixyncSourceKindSchema,
    status: SixyncSourceStatusSchema,
    ownerActorId: z.string().min(1).optional(),
    label: z.string().min(1).optional(),
    transport: SixyncTransportSchema.optional(),
    announcedAt: z.number().nonnegative().optional(),
    readyAt: z.number().nonnegative().optional(),
    stoppedAt: z.number().nonnegative().optional(),
    targetable: z.boolean().optional(),
    metadata: SixyncMetadataSchema.optional(),
}).superRefine((source, ctx) => {
    if (source.metadata?.cfbPlayback !== undefined && !CfbPlaybackStateSchema.safeParse(source.metadata.cfbPlayback).success) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Invalid CFB playback state', path: ['metadata', 'cfbPlayback'] });
    }
});

export const SixyncSceneLayerSchema = z.object({
    layerId: z.string().min(1),
    kind: SixyncSceneLayerKindSchema,
    sourceId: z.string().min(1).optional(),
    zIndex: z.number().int(),
    rect: SixyncRectSchema.optional(),
    visible: z.boolean().default(true),
    targetable: z.boolean().optional(),
    readinessPolicy: z.enum(['required', 'optional', 'hidden_until_ready']).optional(),
    audioPolicy: z.enum(['muted', 'included', 'ducked']).optional(),
    metadata: SixyncMetadataSchema.optional(),
});

export const SixyncProgramLayoutRoleAssignmentSchema = z.object({
    sourceId: z.string().min(1),
    role: SixyncProgramLayoutRoleSchema,
    priority: z.number().int().nonnegative().default(DEFAULT_SIXYNC_PROGRAM_LAYOUT_PRIORITY),
    pinned: z.boolean().default(false),
    visibility: SixyncProgramLayoutVisibilitySchema.default('visible'),
    zIntent: SixyncProgramLayoutZIntentSchema.optional(),
    metadata: SixyncMetadataSchema.optional(),
}).strict();

export const SixyncProgramPresentationSourceSchema = z.object({
    sourceId: z.string().min(1),
    role: SixyncProgramLayoutRoleSchema.optional(),
}).strict();

export const SixyncProgramPresentationBackdropTreatmentSchema = z.object({
    dim: z.number().min(0).max(1).default(0),
    blur: z.number().min(0).max(64).default(0),
}).strict();

export const SixyncProgramPresentationBackdropSourceSchema = SixyncProgramPresentationSourceSchema.extend({
    treatment: SixyncProgramPresentationBackdropTreatmentSchema,
}).strict();

export const SixyncProgramPresentationPipSetSchema = z.object({
    sourceIds: z.array(z.string().min(1)).default([]),
}).strict();

export const SixyncProgramPresentationStateSchema = z.object({
    heroSource: SixyncProgramPresentationSourceSchema.optional(),
    backdropSource: SixyncProgramPresentationBackdropSourceSchema.optional(),
    pipSet: SixyncProgramPresentationPipSetSchema.default({ sourceIds: [] }),
    scaleMode: SixyncProgramPresentationScaleModeSchema.default('cover'),
    metadata: SixyncMetadataSchema.optional(),
}).strict();

export const SixyncProgramLayoutStateSchema = z.object({
    templateId: z.string().min(1),
    layoutMode: z.string().min(1),
    revision: z.number().int().nonnegative(),
    focusSourceId: z.string().min(1).optional(),
    promotedSourceId: z.string().min(1).optional(),
    roleAssignments: z.array(SixyncProgramLayoutRoleAssignmentSchema).default([]),
    presentation: SixyncProgramPresentationStateSchema.optional(),
    metadata: SixyncMetadataSchema.optional(),
}).strict().superRefine((layout, ctx) => {
    if (!layout.promotedSourceId) {
        return;
    }

    const promotedAssignment = layout.roleAssignments.find((assignment) => (
        assignment.sourceId === layout.promotedSourceId && assignment.role === 'speaker'
    ));
    if (!promotedAssignment) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'promotedSourceId must also have a speaker role assignment',
            path: ['promotedSourceId'],
        });
    }
});

export const SixyncSceneSchema = z.object({
    sceneId: z.string().min(1),
    version: z.number().int().nonnegative(),
    layers: z.array(SixyncSceneLayerSchema),
    programLayout: SixyncProgramLayoutStateSchema.optional(),
    metadata: SixyncMetadataSchema.optional(),
});

export const SixyncTargetSurfaceSchema = z.object({
    surfaceId: z.string().min(1),
    kind: SixyncTargetSurfaceKindSchema,
    ready: z.boolean(),
    sourceId: z.string().min(1).optional(),
    layerId: z.string().min(1).optional(),
    rect: SixyncRectSchema.optional(),
    readySeq: z.number().int().nonnegative().optional(),
    lastChangedSeq: z.number().int().nonnegative().optional(),
    metadata: SixyncMetadataSchema.optional(),
});

export const SixyncCueSchema = z.object({
    cueId: z.string().min(1),
    // Wire readers preserve future kinds; publication uses SixyncPublishedCueSchema.
    kind: z.string().min(1),
    status: SixyncCueStatusSchema,
    sourceId: z.string().min(1).optional(),
    targetSurfaceId: z.string().min(1).optional(),
    startedAt: z.number().nonnegative(),
    endsAt: z.number().nonnegative().optional(),
    payload: SixyncMetadataSchema.optional(),
}).superRefine((cue, ctx) => {
    if (!SixyncCueKindSchema.safeParse(cue.kind).success) return;
    if (cue.payload?.cfbNarration !== undefined && (
        cue.kind !== 'topic_transition' || !CfbNarrationCueSchema.safeParse(cue.payload.cfbNarration).success
    )) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Invalid CFB narration identity', path: ['payload', 'cfbNarration'] });
    }
    if (cue.kind === 'agent_viseme_sequence') {
        const result = SixyncAgentVisemeSequencePayloadSchema.safeParse(cue.payload);
        if (!result.success) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Invalid payload for agent_viseme_sequence cue',
                path: ['payload'],
            });
        }
        return;
    }

    if (cue.kind === 'companion_mouth_track') {
        const result = SixyncCompanionMouthTrackPayloadSchema.safeParse(cue.payload);
        if (!result.success) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Invalid payload for companion_mouth_track cue',
                path: ['payload'],
            });
        }
    }
});

/** Closed writer boundary. Readers must remain forward compatible. */
export const SixyncPublishedCueSchema = SixyncCueSchema.superRefine((cue, ctx) => {
    if (!SixyncCueKindSchema.safeParse(cue.kind).success) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Unknown published cue kind', path: ['kind'] });
    }
    if (cue.kind === 'telestration_stroke' && (
        !SixyncTelestrationStrokePayloadSchema.safeParse(cue.payload).success ||
        (cue.status === 'active' && cue.endsAt !== undefined)
    )) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Invalid persistent ink cue', path: ['payload'] });
    }
});

export const SixyncSessionSchema = z.object({
    protocol: SixyncProtocolSchema,
    profile: SixyncProfileSchema,
    roomId: z.string().min(1),
    sessionId: z.string().min(1),
    roomSessionId: z.string().min(1),
    createdAt: z.number().nonnegative(),
    snapshotVersion: z.number().int().nonnegative(),
});

export const SixyncSnapshotSchema = z.object({
    protocol: SixyncProtocolSchema,
    profile: SixyncProfileSchema,
    roomId: z.string().min(1),
    sessionId: z.string().min(1),
    roomSessionId: z.string().min(1),
    snapshotVersion: z.number().int().nonnegative(),
    sceneVersion: z.number().int().nonnegative(),
    serverTime: z.number().nonnegative(),
    perceptionProfileId: SixyncPerceptionProfileSchema,
    sources: z.record(SixyncSourceSchema),
    scene: SixyncSceneSchema,
    targetSurfaces: z.record(SixyncTargetSurfaceSchema).default({}),
    activeSourceIds: z.array(z.string().min(1)).default([]),
    activeCueIds: z.array(z.string().min(1)).default([]),
    cues: z.record(SixyncCueSchema).default({}),
    metadata: SixyncMetadataSchema.optional(),
});

export const SixyncSourceEventPayloadSchema = z.object({
    sourceId: z.string().min(1),
    source: SixyncSourceSchema.optional(),
    reason: z.string().min(1).optional(),
});

export const SixyncSceneLayoutSetPayloadSchema = z.object({
    layout: SixyncProgramLayoutStateSchema,
});

export const SixyncSceneLayerSetPayloadSchema = z.object({
    layer: SixyncSceneLayerSchema,
});

export const SixyncSceneLayerRemovedPayloadSchema = z.object({
    layerId: z.string().min(1),
});

export const SixyncSurfacePayloadSchema = z.object({
    surface: SixyncTargetSurfaceSchema,
});

export const SixyncCueStartedPayloadSchema = z.object({
    cue: SixyncCueSchema,
});

export const SixyncCueCancelledPayloadSchema = z.object({
    cueId: z.string().min(1),
    reason: z.string().min(1).optional(),
});

export const SixyncIntentPayloadSchema = z.object({
    intentId: z.string().min(1),
    metadata: SixyncMetadataSchema.optional(),
});

export const SixyncPerceptionProfilePayloadSchema = z.object({
    perceptionProfileId: SixyncPerceptionProfileSchema,
});

export const SixyncEventPayloadSchemaByOperation = {
    source_announced: SixyncSourceEventPayloadSchema,
    source_ready: SixyncSourceEventPayloadSchema,
    source_stalled: SixyncSourceEventPayloadSchema,
    source_stopped: SixyncSourceEventPayloadSchema,
    scene_layout_set: SixyncSceneLayoutSetPayloadSchema,
    scene_layer_set: SixyncSceneLayerSetPayloadSchema,
    scene_layer_removed: SixyncSceneLayerRemovedPayloadSchema,
    surface_ready: SixyncSurfacePayloadSchema,
    surface_unready: SixyncSurfacePayloadSchema,
    cue_started: SixyncCueStartedPayloadSchema,
    cue_cancelled: SixyncCueCancelledPayloadSchema,
    intent_proposed: SixyncIntentPayloadSchema,
    intent_accepted: SixyncIntentPayloadSchema,
    intent_rejected: SixyncIntentPayloadSchema,
    perception_profile_set: SixyncPerceptionProfilePayloadSchema,
} satisfies Record<SixyncOperation, z.ZodTypeAny>;

export const SixyncEventSchema = z.object({
    protocol: SixyncProtocolSchema,
    profile: SixyncProfileSchema,
    roomId: z.string().min(1),
    sessionId: z.string().min(1),
    roomSessionId: z.string().min(1).optional(),
    eventId: z.string().min(1).optional(),
    seq: z.number().int().positive(),
    baseVersion: z.number().int().nonnegative().optional(),
    op: SixyncOperationSchema,
    scope: SixyncEventScopeSchema,
    actor: SixyncActorSchema,
    serverTime: z.number().nonnegative(),
    reliability: SixyncReliabilitySchema,
    payload: SixyncMetadataSchema,
}).superRefine((event, ctx) => {
    const payloadSchema = SixyncEventPayloadSchemaByOperation[event.op];
    const result = payloadSchema.safeParse(event.payload);

    if (!result.success) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Invalid payload for Sixync operation "${event.op}"`,
            path: ['payload'],
        });
    }
});

export type SixyncProtocol = z.infer<typeof SixyncProtocolSchema>;
export type SixyncProfile = z.infer<typeof SixyncProfileSchema>;
export type SixyncReliability = z.infer<typeof SixyncReliabilitySchema>;
export type SixyncSourceKind = z.infer<typeof SixyncSourceKindSchema>;
export type SixyncSourceStatus = z.infer<typeof SixyncSourceStatusSchema>;
export type SixyncTransportKind = z.infer<typeof SixyncTransportKindSchema>;
export type SixyncSceneLayerKind = z.infer<typeof SixyncSceneLayerKindSchema>;
export type SixyncTargetSurfaceKind = z.infer<typeof SixyncTargetSurfaceKindSchema>;
export type SixyncCueKind = z.infer<typeof SixyncCueKindSchema>;
export type SixyncCueStatus = z.infer<typeof SixyncCueStatusSchema>;
export type SixyncActorKind = z.infer<typeof SixyncActorKindSchema>;
export type SixyncEventScope = z.infer<typeof SixyncEventScopeSchema>;
export type SixyncOperation = z.infer<typeof SixyncOperationSchema>;
export type SixyncPerceptionProfile = z.infer<typeof SixyncPerceptionProfileSchema>;
export type SixyncProgramLayoutRole = z.infer<typeof SixyncProgramLayoutRoleSchema>;
export type SixyncProgramLayoutVisibility = z.infer<typeof SixyncProgramLayoutVisibilitySchema>;
export type SixyncProgramLayoutZIntent = z.infer<typeof SixyncProgramLayoutZIntentSchema>;
export type SixyncMetadata = z.infer<typeof SixyncMetadataSchema>;
export type SixyncProgramAudioClockAnchor = z.infer<typeof SixyncProgramAudioClockAnchorSchema>;
export type SixyncVisemeEvent = z.infer<typeof SixyncVisemeEventSchema>;
export type SixyncAgentVisemeSequencePayload = z.infer<typeof SixyncAgentVisemeSequencePayloadSchema>;
export type SixyncCompanionMouthTrackPayload = z.infer<typeof SixyncCompanionMouthTrackPayloadSchema>;
export type SixyncRect = z.infer<typeof SixyncRectSchema>;
export type SixyncActor = z.infer<typeof SixyncActorSchema>;
export type SixyncTransport = z.infer<typeof SixyncTransportSchema>;
export type SixyncSource = z.infer<typeof SixyncSourceSchema>;
export type SixyncSceneLayer = z.infer<typeof SixyncSceneLayerSchema>;
export type SixyncProgramLayoutRoleAssignment = z.infer<typeof SixyncProgramLayoutRoleAssignmentSchema>;
export type SixyncProgramPresentationScaleMode = z.infer<typeof SixyncProgramPresentationScaleModeSchema>;
export type SixyncProgramPresentationState = z.infer<typeof SixyncProgramPresentationStateSchema>;
export type SixyncProgramLayoutState = z.infer<typeof SixyncProgramLayoutStateSchema>;
export type SixyncScene = z.infer<typeof SixyncSceneSchema>;
export type SixyncTargetSurface = z.infer<typeof SixyncTargetSurfaceSchema>;
export type SixyncCue = z.infer<typeof SixyncCueSchema>;
export type SixyncSession = z.infer<typeof SixyncSessionSchema>;
export type SixyncSnapshot = z.infer<typeof SixyncSnapshotSchema>;
export type SixyncEvent = z.infer<typeof SixyncEventSchema>;
export type SixyncSourceEventPayload = z.infer<typeof SixyncSourceEventPayloadSchema>;
export type SixyncSceneLayoutSetPayload = z.infer<typeof SixyncSceneLayoutSetPayloadSchema>;
export type SixyncSceneLayerSetPayload = z.infer<typeof SixyncSceneLayerSetPayloadSchema>;
export type SixyncSceneLayerRemovedPayload = z.infer<typeof SixyncSceneLayerRemovedPayloadSchema>;
export type SixyncSurfacePayload = z.infer<typeof SixyncSurfacePayloadSchema>;
export type SixyncCueStartedPayload = z.infer<typeof SixyncCueStartedPayloadSchema>;
export type SixyncCueCancelledPayload = z.infer<typeof SixyncCueCancelledPayloadSchema>;
export type SixyncIntentPayload = z.infer<typeof SixyncIntentPayloadSchema>;
export type SixyncPerceptionProfilePayload = z.infer<typeof SixyncPerceptionProfilePayloadSchema>;

export const parseSixyncSession = (value: unknown): SixyncSession => (
    SixyncSessionSchema.parse(value)
);

export const parseSixyncSnapshot = (value: unknown): SixyncSnapshot => (
    SixyncSnapshotSchema.parse(value)
);

export const parseSixyncEvent = (value: unknown): SixyncEvent => (
    SixyncEventSchema.parse(value)
);

export const isSixyncSession = (value: unknown): value is SixyncSession => (
    SixyncSessionSchema.safeParse(value).success
);

export const isSixyncSnapshot = (value: unknown): value is SixyncSnapshot => (
    SixyncSnapshotSchema.safeParse(value).success
);

export const isSixyncEvent = (value: unknown): value is SixyncEvent => (
    SixyncEventSchema.safeParse(value).success
);

/**
 * #3065 — companion conversational state on the wire.
 *
 * `desired_companion_animation_state` in the compositor derives state from viseme
 * activity plus the program layout role. Neither can express "prompt sent, awaiting
 * response", so `Thinking` was unreachable there: a companion who is thinking read, in
 * every recording and simulcast, as one idly watching the room. Three behaviours key off
 * it — gaze drops to 0.30 on-camera with aversions UP 60 % of the time (the recall
 * glance), blink median stretches to 6850 ms, and the resting face takes a `browInnerUp`
 * 0.4 preset.
 *
 * The browser gets this from `AGENT_STATE_CHANGE`; this carries the same value so the
 * two surfaces cannot disagree about what the companion is doing.
 */
export const SIXYNC_COMPANION_STATES = [
    'listening',
    'focused',
    'thinking',
    'speaking',
] as const;

export type SixyncCompanionState = typeof SIXYNC_COMPANION_STATES[number];

export const SixyncCompanionStateSchema = z.enum(SIXYNC_COMPANION_STATES);

/** Payload of a `companion_state` cue. */
export const SixyncCompanionStateCuePayloadSchema = z.object({
    companionId: z.string().min(1),
    state: SixyncCompanionStateSchema,
});

export type SixyncCompanionStateCuePayload = z.infer<typeof SixyncCompanionStateCuePayloadSchema>;

/**
 * Payload of a `companion_emotion` cue — the reply's emotion, as `COMPANION_EMOTION_UPDATE`
 * raised it.
 *
 * `emotion` is the wire string the browser maps (`EMOTION_MORPH_MAPPINGS`), deliberately not an
 * enum here: a reader that does not know a new emotion falls back to a neutral face, which is
 * exactly what the browser does with an unknown key. `raisedAt` is the epoch-ms the room raised
 * it, so a reader can anchor the expression's time course to the room rather than to whenever
 * the cue happened to arrive.
 */
export const SixyncCompanionEmotionCuePayloadSchema = z.object({
    companionId: z.string().min(1),
    emotion: z.string().min(1),
    intensity: z.number().min(0).max(1),
    raisedAt: z.number().nonnegative(),
});

export type SixyncCompanionEmotionCuePayload = z.infer<typeof SixyncCompanionEmotionCuePayloadSchema>;

/**
 * `ListeningState` → the wire vocabulary.
 *
 * Carried faithfully, `focused` included, even though the browser's PROGRAM surface has
 * only `idle | speaking | thinking` today: losing information at the transport is the
 * one thing that cannot be undone later, and a consumer is free to fold `focused` into
 * `listening` — which is exactly what the compositor does, for parity.
 *
 * Numeric literals rather than the enum so this module stays importable by consumers
 * that do not pull in the agent types; the mapping is asserted against `ListeningState`
 * by test.
 */
export const sixyncCompanionStateFromListeningState = (
    listeningState: number | null | undefined,
): SixyncCompanionState | null => {
    switch (listeningState) {
        case 1: return 'listening';
        case 2: return 'focused';
        case 3: return 'thinking';
        case 4: return 'speaking';
        default: return null;
    }
};
